import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import config from '../config';

const ENTITY_TYPES = ['Artist', 'Event', 'Festival', 'Member', 'Promoter', 'Project', 'Venue'];
const PAGE_SIZE = 50;

// Format a UTC timestamp as MM/DD/YYYY HH:MM UTC
function formatTimestamp(ts) {
    if (!ts) return '—';
    const d = new Date(ts);
    const mm  = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd  = String(d.getUTCDate()).padStart(2, '0');
    const yyyy= d.getUTCFullYear();
    const hh  = String(d.getUTCHours()).padStart(2, '0');
    const min = String(d.getUTCMinutes()).padStart(2, '0');
    return `${mm}/${dd}/${yyyy} ${hh}:${min} UTC`;
}

// Format event_date (YYYY-MM-DD) as MM/DD/YYYY
function formatEventDate(dateStr) {
    if (!dateStr) return '';
    const datePart = String(dateStr).split('T')[0]; // handle both YYYY-MM-DD and full ISO
    const d = new Date(datePart + 'T00:00:00Z');
    if (isNaN(d.getTime())) return dateStr; // fallback: return raw value if still unparseable
    return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC' });
}

// Compose the entity display string shown in the Entity Name column
function renderEntityName(row) {
    if (row.entity_type === 'event') {
        const d = row.entity_display || {};
        const parts = [
            d.event_date ? formatEventDate(d.event_date) : null,
            d.venue_name || null,
            d.headliner_name || null,
        ].filter(Boolean);
        return parts.length ? parts.join(' – ') : `Event #${row.entity_id}`;
    }
    return row.entity_display || `[Deleted ${row.entity_type}]`;
}

export default function ChangeLog() {
    const [rows, setRows]           = useState([]);
    const [total, setTotal]         = useState(0);
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState(null);
    const [offset, setOffset]       = useState(0);
    const [revertingId, setRevertingId] = useState(null);
    const [revertMsg, setRevertMsg] = useState(null); // { id, text, ok }
    const [jumpPage, setJumpPage] = useState('');

    // Filter state
    const [entityType, setEntityType] = useState('');
    const [search, setSearch]         = useState('');
    const [dateStart, setDateStart]   = useState('');
    const [dateEnd, setDateEnd]       = useState('');

    // Debounce search input
    const searchTimerRef = useRef(null);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(val);
            setOffset(0);
        }, 300);
    };

    const fetchLog = useCallback(async (currentOffset) => {
        setLoading(true);
        setError(null);
        try {
            const { tokens } = await fetchAuthSession();
            const idToken = tokens.idToken.toString();

            const params = {
                limit: PAGE_SIZE,
                offset: currentOffset,
            };
            if (entityType)      params.entity_type = entityType.toLowerCase();
            if (debouncedSearch) params.search      = debouncedSearch;
            if (dateStart)       params.date_start  = dateStart;
            if (dateEnd)         params.date_end    = dateEnd;

            const res = await axios.get(`${config.API_URL}/admin/change-log`, {
                headers: { Authorization: idToken },
                params,
            });
            setRows(res.data.rows || []);
            setTotal(res.data.total || 0);
        } catch (err) {
            setError('Failed to load change log. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [entityType, debouncedSearch, dateStart, dateEnd]);

    // Refetch whenever filters or page changes
    useEffect(() => {
        fetchLog(offset);
    }, [fetchLog, offset]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setOffset(0);
    }, [entityType, debouncedSearch, dateStart, dateEnd]);

    const handleRevert = async (row) => {
        setRevertingId(row.id);
        setRevertMsg(null);
        try {
            const { tokens } = await fetchAuthSession();
            const idToken = tokens.idToken.toString();
            await axios.post(
                `${config.API_URL}/admin/change-log/${row.id}/revert`,
                {},
                { headers: { Authorization: idToken } }
            );
            setRevertMsg({ id: row.id, text: 'Reverted successfully.', ok: true });
            fetchLog(offset); // refresh list
        } catch (err) {
            const msg = err.response?.data?.error || 'Revert failed.';
            setRevertMsg({ id: row.id, text: msg, ok: false });
        } finally {
            setRevertingId(null);
        }
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);
    const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

    return (
        <div style={S.page}>
            {/* Header */}
            <div style={S.header}>
                <h1 style={S.title}>Change Log</h1>
                <p style={S.subtitle}>Field-level audit trail of all entity edits.</p>
            </div>

            {/* Filter bar */}
            <div style={S.filterBar}>
                {/* Entity type — 10% */}
                <select
                    value={entityType}
                    onChange={e => { setEntityType(e.target.value); setOffset(0); }}
                    style={{ ...S.filterControl, flex: '0 0 10%' }}
                >
                    <option value=''>All Types</option>
                    {ENTITY_TYPES.map(t => (
                        <option key={t} value={t}>{t}</option>
                    ))}
                </select>

                {/* Text search — 50% */}
                <input
                    type='text'
                    placeholder='Search by entity name…'
                    value={search}
                    onChange={handleSearchChange}
                    style={{ ...S.filterControl, flex: '0 0 50%' }}
                />

                {/* Start date — 20% */}
                <input
                    type='date'
                    value={dateStart}
                    onChange={e => { setDateStart(e.target.value); setOffset(0); }}
                    style={{ ...S.filterControl, flex: '0 0 20%', colorScheme: 'dark' }}
                />

                {/* End date — 20% */}
                <input
                    type='date'
                    value={dateEnd}
                    onChange={e => { setDateEnd(e.target.value); setOffset(0); }}
                    style={{ ...S.filterControl, flex: '0 0 20%', colorScheme: 'dark' }}
                />
            </div>

            {/* Status bar */}
            <div style={S.statusBar}>
                {loading ? (
                    <span style={S.statusText}>Loading…</span>
                ) : error ? (
                    <span style={{ ...S.statusText, color: '#e57373' }}>{error}</span>
                ) : (
                    <span style={S.statusText}>
                        {total.toLocaleString()} {total === 1 ? 'entry' : 'entries'}
                        {totalPages > 1 && ` — page ${currentPage} of ${totalPages}`}
                    </span>
                )}
            </div>

            {/* Table */}
            {!loading && !error && rows.length === 0 ? (
                <div style={S.empty}>No change log entries found.</div>
            ) : (
                <div style={S.tableWrap}>
                    <table style={S.table}>
                        <thead>
                            <tr>
                                {['User', 'Date & Time (UTC)', 'Type', 'Entity Name', 'Field', 'Old Value', 'New Value', ''].map(h => (
                                    <th key={h} style={S.th}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => {
                                const isRevert = row.is_revert;
                                return (
                                    <tr
                                        key={row.id}
                                        style={{
                                            ...S.tr,
                                            borderLeft: isRevert ? '3px solid #bb86fc' : '3px solid transparent',
                                        }}
                                    >
                                        <td style={S.td}>{row.display_name || row.changed_by}</td>
                                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{formatTimestamp(row.changed_at)}</td>
                                        <td style={S.td}>{row.entity_type.charAt(0).toUpperCase() + row.entity_type.slice(1)}</td>
                                        <td style={S.td}>{renderEntityName(row)}</td>
                                        <td style={S.td}>
                                            {isRevert ? (
                                                <span>
                                                    <span style={S.revertBadge}>↩ Revert</span>
                                                    {' '}{row.field_name}
                                                </span>
                                            ) : (
                                                row.field_name
                                            )}
                                        </td>
                                        <td style={S.tdValue}>{row.old_value ?? <span style={S.nullVal}>—</span>}</td>
                                        <td style={S.tdValue}>{row.new_value ?? <span style={S.nullVal}>—</span>}</td>
                                        <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            {revertMsg && revertMsg.id === row.id && (
                                                <span style={{
                                                    fontSize: '11px',
                                                    color: revertMsg.ok ? '#81c784' : '#e57373',
                                                    marginRight: '8px',
                                                }}>
                                                    {revertMsg.text}
                                                </span>
                                            )}
                                            <RevertButton
                                                row={row}
                                                loading={revertingId === row.id}
                                                onRevert={handleRevert}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div style={S.pagination}>
                    <button
                        onClick={() => { setOffset(0); setJumpPage(''); }}
                        disabled={offset === 0}
                        style={{ ...S.pageBtn, opacity: offset === 0 ? 0.4 : 1 }}
                    >
                        «
                    </button>
                    <button
                        onClick={() => { setOffset(Math.max(0, offset - PAGE_SIZE)); setJumpPage(''); }}
                        disabled={offset === 0}
                        style={{ ...S.pageBtn, opacity: offset === 0 ? 0.4 : 1 }}
                    >
                        ‹ Prev
                    </button>
                    <span style={S.pageInfo}>
                        Page {currentPage} of {totalPages}
                    </span>
                    {totalPages > 10 && (
                        <span style={S.jumpWrap}>
                            <span style={S.jumpLabel}>Go to</span>
                            <input
                                type='number'
                                min={1}
                                max={totalPages}
                                value={jumpPage}
                                onChange={e => setJumpPage(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        const p = parseInt(jumpPage, 10);
                                        if (!isNaN(p) && p >= 1 && p <= totalPages) {
                                            setOffset((p - 1) * PAGE_SIZE);
                                            setJumpPage('');
                                        }
                                    }
                                }}
                                style={S.jumpInput}
                                placeholder='#'
                            />
                            <button
                                onClick={() => {
                                    const p = parseInt(jumpPage, 10);
                                    if (!isNaN(p) && p >= 1 && p <= totalPages) {
                                        setOffset((p - 1) * PAGE_SIZE);
                                        setJumpPage('');
                                    }
                                }}
                                style={S.pageBtn}
                            >
                                Go
                            </button>
                        </span>
                    )}
                    <button
                        onClick={() => { setOffset(offset + PAGE_SIZE); setJumpPage(''); }}
                        disabled={offset + PAGE_SIZE >= total}
                        style={{ ...S.pageBtn, opacity: offset + PAGE_SIZE >= total ? 0.4 : 1 }}
                    >
                        Next ›
                    </button>
                    <button
                        onClick={() => { setOffset((totalPages - 1) * PAGE_SIZE); setJumpPage(''); }}
                        disabled={offset + PAGE_SIZE >= total}
                        style={{ ...S.pageBtn, opacity: offset + PAGE_SIZE >= total ? 0.4 : 1 }}
                    >
                        »
                    </button>
                </div>
            )}
        </div>
    );
}

function RevertButton({ row, loading, onRevert }) {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            onClick={() => onRevert(row)}
            disabled={loading}
            style={{
                backgroundColor: '#2c2c2c',
                border: `1px solid ${hovered ? '#bb86fc' : '#555'}`,
                borderRadius: '4px',
                color: loading ? '#666' : '#e0e0e0',
                fontSize: '12px',
                padding: '4px 10px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'border-color 0.15s',
                whiteSpace: 'nowrap',
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {loading ? '…' : 'Revert'}
        </button>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
    page: {
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '40px 24px',
        color: '#e0e0e0',
        fontFamily: 'sans-serif',
    },
    header: {
        marginBottom: '28px',
    },
    title: {
        fontSize: '28px',
        fontWeight: 'bold',
        color: '#bb86fc',
        margin: '0 0 6px 0',
    },
    subtitle: {
        fontSize: '14px',
        color: '#777',
        margin: 0,
    },
    filterBar: {
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
        flexWrap: 'nowrap',
    },
    filterControl: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        color: '#e0e0e0',
        fontSize: '13px',
        padding: '8px 10px',
        height: '36px',
        boxSizing: 'border-box',
        outline: 'none',
        minWidth: 0,
    },
    statusBar: {
        marginBottom: '12px',
    },
    statusText: {
        fontSize: '13px',
        color: '#777',
    },
    empty: {
        padding: '48px',
        textAlign: 'center',
        color: '#555',
        fontSize: '14px',
    },
    tableWrap: {
        overflowX: 'auto',
        borderRadius: '8px',
        border: '1px solid #2a2a2a',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '13px',
    },
    th: {
        backgroundColor: '#1e1e2e',
        color: '#bb86fc',
        fontSize: '11px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: '0.8px',
        padding: '10px 12px',
        textAlign: 'left',
        borderBottom: '1px solid #333',
        whiteSpace: 'nowrap',
    },
    tr: {
        borderBottom: '1px solid #2a2a2a',
        transition: 'background-color 0.1s',
    },
    td: {
        padding: '10px 12px',
        color: '#e0e0e0',
        verticalAlign: 'top',
        fontSize: '13px',
    },
    tdValue: {
        padding: '10px 12px',
        color: '#ccc',
        verticalAlign: 'top',
        fontSize: '13px',
        maxWidth: '200px',
        wordBreak: 'break-word',
    },
    nullVal: {
        color: '#444',
    },
    revertBadge: {
        fontSize: '11px',
        color: '#bb86fc',
        backgroundColor: '#2a1f3d',
        border: '1px solid #bb86fc',
        borderRadius: '3px',
        padding: '1px 5px',
        marginRight: '4px',
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        marginTop: '24px',
        flexWrap: 'nowrap',
    },
    pageBtn: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        color: '#e0e0e0',
        fontSize: '13px',
        padding: '6px 12px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    pageInfo: {
        fontSize: '13px',
        color: '#aaa',
        whiteSpace: 'nowrap',
        padding: '0 8px',
    },
    jumpWrap: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginLeft: '8px',
    },
    jumpLabel: {
        fontSize: '13px',
        color: '#777',
        whiteSpace: 'nowrap',
    },
    jumpInput: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        color: '#e0e0e0',
        fontSize: '13px',
        padding: '6px 8px',
        width: '60px',
        textAlign: 'center',
        outline: 'none',
    },
};
